import type { ReactNode } from 'react';
import { OUTCOMES, OUTCOME_COLORS, OUTCOME_LABELS, type CallFilterState, type LocationSummary, type Outcome } from '../types';
import { daysAgo, toDateInput } from '../format';
import { TagFilter, type TagOption } from './TagFilter';
import { DateRangeField, FilterPanel, SwitchField } from './FilterPanel';

interface Caller {
  userId: string | null;
  userName: string | null;
  locationId: string;
  total: number;
}

interface Props {
  filters: CallFilterState;
  onChange: (next: CallFilterState) => void;
  onReset?: () => void;
  /** The saved-views menu, shown in the panel header. */
  views?: ReactNode;
  locations: LocationSummary[];
  callers?: Caller[];
  showCallerFilter?: boolean;
  tagOptions?: TagOption[];
  showSearch?: boolean;
}

const PRESETS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
];

/**
 * Above the content it scopes. Date range first — it is the filter every
 * reader reaches for.
 */
export function FilterBar({
  filters,
  onChange,
  onReset,
  views,
  locations,
  callers = [],
  showCallerFilter,
  showSearch,
  tagOptions = [],
}: Props) {
  const today = toDateInput(new Date());

  function toggleOutcome(outcome: Outcome) {
    const next = filters.outcomes.includes(outcome)
      ? filters.outcomes.filter((item) => item !== outcome)
      : [...filters.outcomes, outcome];
    onChange({ ...filters, outcomes: next });
  }

  // When the toggle is on, the picker offers only the sub-accounts the figures
  // are drawn from, so the list and the numbers never disagree.
  const visibleLocations = filters.activeClientsOnly
    ? locations.filter((location) => location.isActiveClient)
    : locations;

  const activeCount =
    (filters.locationIds.length ? 1 : 0) +
    (filters.userIds.length ? 1 : 0) +
    (filters.direction ? 1 : 0) +
    (filters.outcomes.length ? 1 : 0) +
    (filters.tags.length ? 1 : 0) +
    (showSearch && filters.search.trim() ? 1 : 0) +
    (filters.activeClientsOnly ? 0 : 1);

  return (
    <FilterPanel actions={views} activeCount={activeCount} onReset={onReset}>
      <DateRangeField
        id="filter-from"
        label="Date range"
        from={filters.from}
        to={filters.to}
        max={today}
        presets={PRESETS.map((preset) => ({
          label: preset.label,
          from: preset.days === 0 ? today : daysAgo(preset.days),
          to: today,
        }))}
        onChange={(from, to) => onChange({ ...filters, from, to })}
      />

      <SwitchField
        id="filter-active"
        label="Clients"
        text="Active only"
        checked={filters.activeClientsOnly}
        onChange={(checked) => onChange({ ...filters, activeClientsOnly: checked, locationIds: [] })}
      />

      <div className="field">
        <label htmlFor="filter-location">Sub-account</label>
        <select
          id="filter-location"
          value={filters.locationIds[0] ?? ''}
          onChange={(event) =>
            onChange({ ...filters, locationIds: event.target.value ? [event.target.value] : [], userIds: [] })
          }
        >
          <option value="">
            {filters.activeClientsOnly ? 'All active clients' : 'All sub-accounts'}
          </option>
          {visibleLocations.map((location) => (
            <option value={location.id} key={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </div>

      {showCallerFilter ? (
        <div className="field">
          <label htmlFor="filter-user">Made by</label>
          <select
            id="filter-user"
            value={filters.userIds[0] ?? ''}
            onChange={(event) => onChange({ ...filters, userIds: event.target.value ? [event.target.value] : [] })}
          >
            <option value="">Anyone</option>
            <option value="unassigned">Automated / unassigned</option>
            {callers
              .filter((caller) => caller.userId)
              .map((caller) => (
                <option value={caller.userId!} key={`${caller.userId}-${caller.locationId}`}>
                  {caller.userName ?? caller.userId} ({caller.total})
                </option>
              ))}
          </select>
        </div>
      ) : null}

      <div className="field">
        <label>Direction</label>
        <div className="segmented" role="group" aria-label="Direction">
          {(
            [
              ['', 'Both'],
              ['outbound', 'Outbound'],
              ['inbound', 'Inbound'],
            ] as [CallFilterState['direction'], string][]
          ).map(([value, label]) => (
            <button
              type="button"
              key={value || 'both'}
              aria-pressed={filters.direction === value}
              onClick={() => onChange({ ...filters, direction: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {showSearch ? (
        <div className="field field-grow">
          <label htmlFor="filter-search">Search</label>
          <input
            id="filter-search"
            type="search"
            placeholder="Contact or user…"
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
          />
        </div>
      ) : null}

      <div className="field">
        <label>Outcome</label>
        <div className="chips">
          {OUTCOMES.map((outcome) => (
            <button
              type="button"
              className="chip"
              key={outcome}
              aria-pressed={filters.outcomes.includes(outcome)}
              onClick={() => toggleOutcome(outcome)}
            >
              <span className="swatch" style={{ background: OUTCOME_COLORS[outcome] }} aria-hidden="true" />
              {OUTCOME_LABELS[outcome]}
            </button>
          ))}
        </div>
      </div>

      <TagFilter
        id="filter-tags"
        options={tagOptions}
        selected={filters.tags}
        match={filters.tagMatch}
        onChange={(tags, tagMatch) => onChange({ ...filters, tags, tagMatch })}
      />
    </FilterPanel>
  );
}
